import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  useMediaQuery,
  Alert as MuiAlert,
  Snackbar,
  AlertTitle
} from '@mui/material';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { LoadingContext, LoadingBar, DefaultURl } from '../../M_Components';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import PropTypes from 'prop-types';

// Realm-scoped storage keys
import { STORAGE_CARRIER, STORAGE_USER } from 'src/components/auth/storageKeys';

// Realm-scoped Redux store factory
import { createStoreForRealm } from '../M_Store/index';

/*
 * Root shell component responsible for:
 * - Redux Provider + PersistGate
 * - Global loading / toast context
 * - Global Axios interceptors for:
 *   - injecting Authorization header based on active realm
 *   - rewriting specific endpoints from "/x?y" to "/x/query?y" when requested
 *   - handling 401 responses by clearing the active session and redirecting to login
 */
export function MRouter({
  Routing,
  url,
  primaryColor,
  secondaryColor,
  tertiaryColor,
  endpoints
}) {
  // ---------------------------------------------------------------------------
  // Global UI state
  // ---------------------------------------------------------------------------
  const [isLoad, setIsLoad] = useState(false);

  const [isOpenToast, setIsOpenToast] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [colorSnack, setColorSnack] = useState('');
  const [alertFunction, setAlertFunction] = useState('');

  const [useComma, setUseComma] = useState(true);
  const [passValue, setPassValue] = useState(new Map());

  const { t } = useTranslation();

  const DEFAULTURL = url || DefaultURl;
  const matches = useMediaQuery('(min-width:1200px)');
  endpoints = endpoints || [];

  // ---------------------------------------------------------------------------
  // Realm helpers
  // ---------------------------------------------------------------------------

  /**
   * Detects the active realm from the current hash route.
   *
   * Rules:
   * - "#/carrier/..." => carrier realm
   * - everything else => user realm
   *
   * This is important because:
   * - each realm stores its token in different storage keys;
   * - each realm has its own persisted Redux store;
   * - each realm must redirect to its own login page on 401.
   *
   * @returns {'carrier'|'user'} The detected active realm.
   */
  const getActiveRealm = React.useCallback(() => {
    const h = window.location.hash || '';
    return h.startsWith('#/carrier') ? 'carrier' : 'user';
  }, []);

  /**
   * Freeze the realm for the lifetime of this browser tab load.
   *
   * This avoids recreating the store/persistor on every render or navigation.
   */
  const [realm] = useState(() => getActiveRealm());

  /**
   * Create a realm-scoped Redux store/persistor.
   *
   * This prevents persisted UI state from leaking between:
   * - normal user area
   * - carrier area
   */
  const { store, persistor } = useMemo(() => createStoreForRealm(realm), [realm]);

  /**
   * Retrieves the correct JWT token from localStorage for the active realm.
   *
   * User realm:
   * - primary source is STORAGE_USER.token
   *
   * Carrier realm:
   * - primary source is STORAGE_CARRIER.token
   *
   * @returns {string|null} The active realm token, or null when absent.
   */
  const getRealmToken = React.useCallback(() => {
    const active = getActiveRealm();

    if (active === 'carrier') {
      return localStorage.getItem(STORAGE_CARRIER?.token) || null;
    }

    return localStorage.getItem(STORAGE_USER?.token) || null;
  }, [getActiveRealm]);

  /**
   * Builds an absolute browser URL targeting a hash route.
   *
   * Example:
   * - "/login" => "https://host/#/login"
   *
   * @param {string} hashPath - Hash path to navigate to.
   * @returns {string} Absolute URL.
   */
  const buildHashUrl = React.useCallback((hashPath) => {
    return `${window.location.origin}/#${hashPath.startsWith('/') ? hashPath : `/${hashPath}`}`;
  }, []);

  /**
   * Redirects the browser to the correct login page for the given realm.
   *
   * Rules:
   * - carrier => "#/carrier/login"
   * - user    => "#/login"
   *
   * A guard prevents redundant reloads if already on the target URL.
   *
   * @param {'carrier'|'user'} activeRealm - Realm to redirect.
   */
  const redirectToRealmLogin = React.useCallback(
    (activeRealm) => {
      const target =
        activeRealm === 'carrier'
          ? buildHashUrl('/carrier/login')
          : buildHashUrl('/login');

      const current = window.location.href;

      if (current !== target) {
        window.location.href = target;
      }
    },
    [buildHashUrl]
  );

  /**
   * Clears persisted authentication/session information for the given realm.
   *
   * User realm:
   * - clears STORAGE_USER scoped keys
   * - also clears legacy keys that are still written by Login
   *
   * Carrier realm:
   * - clears STORAGE_CARRIER scoped keys only
   *
   * This is required on 401 so the app cannot continue using an expired/invalid token.
   *
   * @param {'carrier'|'user'} activeRealm - Realm whose session should be cleared.
   */
  const clearRealmSession = React.useCallback((activeRealm) => {
    if (activeRealm === 'carrier') {
      localStorage.removeItem(STORAGE_CARRIER?.token);
      localStorage.removeItem(STORAGE_CARRIER?.expiresUtc);
      localStorage.removeItem(STORAGE_CARRIER?.menus);
      localStorage.removeItem(STORAGE_CARRIER?.permissions);
      localStorage.removeItem(STORAGE_CARRIER?.session);
      localStorage.removeItem(STORAGE_CARRIER?.profile);
      return;
    }

    // User scoped keys
    localStorage.removeItem(STORAGE_USER?.token);
    localStorage.removeItem(STORAGE_USER?.menus);
    localStorage.removeItem(STORAGE_USER?.permissions);
    localStorage.removeItem(STORAGE_USER?.profile);

    // Legacy keys still used elsewhere in the app
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    localStorage.removeItem('defaultAccountName');
    localStorage.removeItem('defaultShipperId');
    localStorage.removeItem('defaultShipperName');
    localStorage.removeItem('userId');
    localStorage.removeItem('cargofiveApiKeyId');
  }, []);

  /**
   * Determines whether a request URL belongs to an auth-free endpoint.
   *
   * Auth-free endpoints must not trigger:
   * - Authorization header enforcement logic
   * - 401 forced logout / redirect logic
   *
   * This includes login, forgot-password, and public carrier invite flows.
   *
   * @param {string} urlLower - Request URL already normalized to lower-case.
   * @returns {boolean} True when the request is auth-free.
   */
  const isAuthFreeRequest = React.useCallback((urlLower) => {
    return (
      // USER LOGIN / FORGOT PASSWORD
      urlLower.includes('identity/login') ||
      urlLower.includes('identity/forgetpassword') ||

      // CARRIER LOGIN / FORGOT PASSWORD
      urlLower.includes('carrieridentity/login') ||
      urlLower.includes('carrieridentity/password/forgot') ||
      urlLower.includes('carrieridentity/forgetpassword') ||

      // CARRIER INVITE FLOWS (PUBLIC)
      urlLower.includes('/carrier-invite/') ||
      urlLower.includes('carrierinvitespot') ||
      urlLower.includes('carrierinvitephase')
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Axios interceptors
  // ---------------------------------------------------------------------------

  /**
   * Registers global Axios interceptors responsible for:
   * - adding the Bearer token to protected requests;
   * - rewriting query endpoints when requested;
   * - forcing logout + redirect on protected 401 responses.
   *
   * Important behavior:
   * - we do not redirect in the request interceptor when token is missing,
   *   because that can leave loading flows stuck in callers;
   * - we let the backend answer with 401 and handle the redirect centrally
   *   in the response interceptor;
   * - on 401 we clear the active realm session before redirecting.
   */
  useEffect(() => {
    let isHandlingUnauthorized = false;

    const reqId = axios.interceptors.request.use((config) => {
      const token = getRealmToken();
      const reqUrl = (config.url || '').toLowerCase();
      const authFree = isAuthFreeRequest(reqUrl);

      // Attach Authorization header only for protected endpoints.
      if (token && !authFree) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Optional rewrite:
      // "/endpoint?x=1" -> "/endpoint/query?x=1"
      if (endpoints && endpoints.length > 0) {
        let patched = false;

        for (const end of endpoints) {
          if (patched) break;

          if (config.url?.includes(`/${end}?`)) {
            config.url = config.url.replace('?', '/query?');
            patched = true;
          }
        }
      }

      return config;
    });

    const resId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const urlLower = error?.config?.url?.toLowerCase() || '';
        const authFree = isAuthFreeRequest(urlLower);

        // Only protected endpoints should trigger forced logout on 401.
        if (status === 401 && !authFree && !isHandlingUnauthorized) {
          isHandlingUnauthorized = true;

          const active = getActiveRealm();

          // Clear the active realm session first.
          clearRealmSession(active);

          // Clear in-memory shared transient values.
          setPassValue(new Map());

          // Redirect to the proper login page.
          redirectToRealmLogin(active);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [
    endpoints,
    getActiveRealm,
    getRealmToken,
    isAuthFreeRequest,
    clearRealmSession,
    redirectToRealmLogin
  ]);

  // ---------------------------------------------------------------------------
  // Loader timeout safeguard
  // ---------------------------------------------------------------------------

  /**
   * Fails safe when the global loading overlay remains active for too long.
   *
   * This protects against broken request flows that forget to switch loading off.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoad === true) {
        setIsLoad(false);
        toastMessages('error', 'Load Timeout', 'ERROR ');
      }
    }, 300000); // 5 minutes

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoad]);

  // ---------------------------------------------------------------------------
  // Toast / snackbar helpers
  // ---------------------------------------------------------------------------

  /**
   * Opens a global snackbar/toast message.
   *
   * @param {'error'|'success'|'info'|string} type - Severity/type.
   * @param {string} alert - Highlighted message.
   * @param {string} string - Secondary message content.
   */
  const toastMessages = (type, alert, string) => {
    setIsOpenToast(true);
    setColorSnack(type);
    setAlertMessage(alert);
    setAlertFunction(string);
  };

  /**
   * Closes the global snackbar unless the reason is clickaway.
   *
   * @param {string} reason - Snackbar close reason.
   */
  const handleClose = (reason) => {
    if (reason === 'clickaway') return;
    setIsOpenToast(false);
  };

  /**
   * Resolves the localized alert title from the current snackbar severity.
   *
   * @returns {string} Localized snackbar title.
   */
  function alertTitleMessage() {
    if (colorSnack === 'error') return t('Error');
    if (colorSnack === 'success') return t('Success');
    if (colorSnack === 'info') return t('InformationLabel');
    return '';
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <LoadingContext.Provider
          value={useMemo(
            () => ({
              isLoad,
              setIsLoad,
              isOpenToast,
              setIsOpenToast,
              alertMessage,
              setAlertMessage,
              colorSnack,
              setColorSnack,
              alertFunction,
              setAlertFunction,
              toastMessages,
              handleClose,
              useComma,
              setUseComma,
              DEFAULTURL,
              primaryColor,
              secondaryColor,
              tertiaryColor,
              passValue,
              setPassValue
            }),
            [
              isLoad,
              isOpenToast,
              alertMessage,
              colorSnack,
              alertFunction,
              useComma,
              passValue,
              DEFAULTURL,
              primaryColor,
              secondaryColor,
              tertiaryColor
            ]
          )}
        >
          {/* Global loading overlay */}
          <div
            style={
              isLoad
                ? {
                  position: 'fixed',
                  height: '100vh',
                  width: '100%',
                  zIndex: '5000000',
                  top: '0',
                  backgroundColor: 'var(--color-grey-light)',
                  opacity: '0.5',
                  overflow: 'hidden'
                }
                : {
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  display: 'none'
                }
            }
          >
            <div style={matches ? { height: '50px' } : { height: '100px' }} />
            <LoadingBar />
          </div>

          {/* Application routing tree */}
          {Routing}

          {/* Global snackbar */}
          <Snackbar
            disableWindowBlurListener={true}
            open={isOpenToast}
            autoHideDuration={5000}
            onClose={handleClose}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              onClose={handleClose}
              severity={colorSnack || null}
              sx={{ width: '100%' }}
            >
              <AlertTitle style={{ textTransform: 'capitalize' }}>
                {alertTitleMessage()}
              </AlertTitle>
              {alertFunction}
              <b>{alertMessage}</b>
            </MuiAlert>
          </Snackbar>
        </LoadingContext.Provider>
      </PersistGate>
    </Provider>
  );
}

MRouter.propTypes = {
  Routing: PropTypes.element,
  url: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  tertiaryColor: PropTypes.string,
  endpoints: PropTypes.array
};